import { MetricObjectType } from "./metricExplainerUtils";
import styled from "styled-components/macro";
import {
  FONT_SIZE_SMALL,
  FONT_SIZE_EXTRA_SMALL,
  PNL_CATEGORIES_TO_COLORS_MAP,
  BREAKPOINT_TABLET,
} from "../../../constants/constants";
import { PnlCategoryLabelsEnum } from "../../../types";
import { camelCase } from "lodash";
import { EquationType } from "./metricExplainerTypes";

const MetricObjectStyles = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .title {
    align-self: flex-start;
    font-size: ${FONT_SIZE_SMALL - 2}px;
    font-weight: 700;
    line-height: 16px;
    padding: 2em 0 0;
  }

  .metricFormula {
    display: grid;
    grid-template-columns: 2fr auto 1fr;
    font-size: ${FONT_SIZE_EXTRA_SMALL}px;
    grid-column-gap: 1em;
    justify-content: center;
    align-items: center;
  }

  .equals {
    font-size: ${FONT_SIZE_SMALL - 2}px;
  }

  .math {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-column-gap: 5px;

    .element {
      padding-left: 5px;
    }
  }

  .numerator,
  .denominator,
  .denominatorRectangle {
    display: flex;
    justify-content: center;
    padding: 10px 0;
  }

  .denominator,
  .denominatorRectangle {
    border-top: 1px solid black;
  }

  &&& {
    @media print {
      .math {
        grid-column-gap: 2px;

        .element {
          padding-left: 2px;
        }
      }

      .metricFormula {
        grid-template-columns: 2fr auto 1fr;
        grid-column-gap: 1em;
      }

      .numerator,
      .denominator,
      .denominatorRectangle {
        flex-direction: row;
        padding: 10px 0;
      }
    }
  }

  @media (max-width: ${BREAKPOINT_TABLET}px) {
    .title {
      font-size: ${FONT_SIZE_SMALL - 4}px;
    }

    .metricFormula {
      grid-template-columns: auto;
      grid-template-rows: 2fr auto 1fr;
      text-align: center;
      font-size: ${FONT_SIZE_SMALL - 4}px;
    }
    .numerator,
    .denominator {
      flex-direction: column;
      align-items: center;
    }
  }
`;

const RectangleStyles = styled.div<{ color: string }>`
  height: 20px;
  width: 50px;
  background-color: ${props => props.color};
  border: 1px solid black;

  @media print, (max-width: ${BREAKPOINT_TABLET}px) {
    height: 10px;
    width: 25px;
  }
`;

const Rectangle = ({ category }: { category: PnlCategoryLabelsEnum }) => {
  const color = PNL_CATEGORIES_TO_COLORS_MAP[camelCase(category)];
  return <RectangleStyles color={color} />;
};

const getEquationFromArr = (list: EquationType, showRectangle = false) => {
  return list.map(({ name, symbol = "+" }, index) => {
    const element = showRectangle ? (
      <Rectangle category={name as PnlCategoryLabelsEnum} />
    ) : (
      name
    );
    return (
      <div key={name} className="math">
        <div className="element">{element}</div>
        {!showRectangle && <div>{list.length - 1 === index ? "" : symbol}</div>}
      </div>
    );
  });
};

export default function MetricExplainerRow(
  metricObjOrString:
    | MetricObjectType
    | { name: string; description: string; nameTranslated: string }
) {
  if ("description" in metricObjOrString) {
    return (
      <MetricDescriptionStyles>
        <div className="name">
          {metricObjOrString.nameTranslated ?? metricObjOrString.name}
        </div>
        <div className="description">{metricObjOrString.description}</div>
      </MetricDescriptionStyles>
    );
  }
  const { longname, name, nameTranslated, numerator, denominator } =
    metricObjOrString as MetricObjectType;

  const hasEquation = [...numerator, ...denominator].every(item =>
    Object.values(PnlCategoryLabelsEnum).map(String).includes(item.name)
  );
  return (
    <MetricObjectStyles>
      <div className="title">{longname || nameTranslated || name}</div>
      <div className="metricFormula">
        <div>
          <div className="numerator">{getEquationFromArr(numerator)}</div>
          <div className="denominator">
            {getEquationFromArr(denominator, false)}
          </div>
        </div>
        {hasEquation ? (
          <>
            <div className="equals">=</div>
            <div>
              <div className="numerator">
                {getEquationFromArr(numerator, true)}
              </div>
              <div className="denominatorRectangle">
                {getEquationFromArr(denominator, true)}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </MetricObjectStyles>
  );
}
const MetricDescriptionStyles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  padding: 0.5em 0;
  .name {
    font-size: 16px;
    font-weight: bold;
  }
  .description {
  }
`;
